import { trackEvent } from './analytics';
import { API_URL } from './platform';
import { bodyTokens, tokenStore } from './tokenStore';

export interface ApiErrorBody {
  code: string;
  message: string;
  details?: Record<string, unknown>;
}

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly details?: Record<string, unknown>,
  ) {
    super(message);
  }
}

export interface SelfUser {
  id: string;
  username: string;
  displayName: string;
  avatar: string | null;
  avatarUrl: string | null;
  country: string | null;
  isGuest: boolean;
  isPremium: boolean;
  rating: number;
  email: string | null;
  locale: string;
  skin: string;
  theme: string;
  /** Death effect (see DEATH_EFFECTS). */
  deathEffect: string;
  premiumUntil: string | null;
  hasPassword: boolean;
  linked: { google: boolean; apple: boolean };
  coins: number;
  /** Owned shop/pass/season cosmetics as "skin:id" / "theme:id". */
  items: string[];
  clan: { id: string; tag: string; name: string; badge: string; role: 'owner' | 'officer' | 'member' } | null;
  stats: {
    rating: number;
    gamesPlayed: number;
    wins: number;
    losses: number;
    draws: number;
    winRate: number;
    kills: number;
    bestLength: number;
    bestScore: number;
    currentStreak: number;
    bestStreak: number;
    casualGames: number;
    casualWins: number;
    arenaBest: number;
    arenaRuns: number;
  };
  createdAt: string;
}

export interface ServerConfig {
  region: string;
  voice: boolean;
  webPayments: boolean;
  oauth: { google: boolean; apple: boolean };
  vapidPublicKey: string | null;
  features: { avatarChange: boolean };
}

let configPromise: Promise<ServerConfig | null> | null = null;
/** Public server config, fetched once. */
export const serverConfig = () => (configPromise ??= api<ServerConfig>('/api/config', { auth: false }).catch(() => null));

interface AuthResponse {
  user: SelfUser;
  accessToken: string;
  accessExpiresAt: number;
  refreshToken?: string;
}

let accessToken: string | null = null;
let accessExpiresAt = 0;
let refreshing: Promise<AuthResponse | null> | null = null;
let onSessionLost: () => void = () => {};

export const setSessionLostHandler = (fn: () => void) => (onSessionLost = fn);
export const getAccessToken = () => accessToken;

/** Web only: remembers that an httpOnly refresh cookie *may* exist, so first-time visitors skip a pointless 401. */
const HINT = 'snakefeast.session';
const hint = {
  get: () => {
    try {
      return localStorage.getItem(HINT) === '1';
    } catch {
      return true;
    }
  },
  set: (on: boolean) => {
    try {
      if (on) localStorage.setItem(HINT, '1');
      else localStorage.removeItem(HINT);
    } catch {
      /* private mode */
    }
  },
};

const baseHeaders = (): Record<string, string> => ({
  'content-type': 'application/json',
  'x-client': bodyTokens ? 'native' : 'web',
});

async function rawFetch(path: string, init: RequestInit): Promise<Response> {
  return fetch(API_URL + path, { ...init, credentials: 'include' });
}

async function parseError(res: Response): Promise<ApiError> {
  const body = (await res.json().catch(() => null)) as { error?: ApiErrorBody } | null;
  return new ApiError(res.status, body?.error?.code ?? 'internal', body?.error?.message ?? res.statusText, body?.error?.details);
}

export async function adoptSession(r: AuthResponse): Promise<SelfUser> {
  accessToken = r.accessToken;
  accessExpiresAt = r.accessExpiresAt;
  hint.set(true);
  await tokenStore.set(r.refreshToken);
  return r.user;
}

/** Single-flight refresh: concurrent 401s share one refresh request. */
export function refreshSession(): Promise<AuthResponse | null> {
  refreshing ??= (async () => {
    try {
      const stored = await tokenStore.get();
      if (bodyTokens ? !stored : !hint.get()) return null;
      const res = await rawFetch('/api/auth/refresh', {
        method: 'POST',
        headers: baseHeaders(),
        body: JSON.stringify(stored ? { refreshToken: stored } : {}),
      });
      if (!res.ok) {
        if (res.status === 401) {
          hint.set(false);
          await tokenStore.clear();
        }
        return null;
      }
      const data = (await res.json()) as AuthResponse;
      await adoptSession(data);
      return data;
    } catch {
      return null;
    } finally {
      refreshing = null;
    }
  })();
  return refreshing;
}

/** Fresh access token (refreshing ~1 min before expiry). Used for WebSocket auth too. */
export async function ensureAccessToken(): Promise<string | null> {
  if (accessToken && Date.now() < accessExpiresAt - 60_000) return accessToken;
  const r = await refreshSession();
  return r?.accessToken ?? null;
}

export async function api<T>(path: string, opts: { method?: string; body?: unknown; auth?: boolean } = {}): Promise<T> {
  const needAuth = opts.auth !== false;
  const doFetch = async () => {
    const headers = baseHeaders();
    if (needAuth) {
      const t = await ensureAccessToken();
      if (t) headers.authorization = `Bearer ${t}`;
    } else if (accessToken) {
      headers.authorization = `Bearer ${accessToken}`; // lets register/oauth upgrade a guest
    }
    return rawFetch(path, {
      method: opts.method ?? (opts.body !== undefined ? 'POST' : 'GET'),
      headers,
      body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
    });
  };
  let res = await doFetch();
  if (res.status === 401 && needAuth) {
    if (await refreshSession()) res = await doFetch();
    if (res.status === 401) onSessionLost();
  }
  if (!res.ok) throw await parseError(res);
  return (await res.json()) as T;
}

/**
 * Register and Google sign-in upgrade the current guest in place, which the server only does
 * for a valid guest access token. Refresh it first if it has run out (the sign-up form may
 * have been open a while), otherwise a brand-new account is made and the guest's progress is left behind.
 */
async function freshGuestToken(): Promise<void> {
  if (accessToken) await ensureAccessToken().catch(() => null);
}

export const authApi = {
  // Each counted in the analytics once it worked: how many start playing as a guest, and how many sign up.
  guest: (locale: string) =>
    api<AuthResponse>('/api/auth/guest', { body: { locale }, auth: false })
      .then(adoptSession)
      .then((r) => (trackEvent('guest_start'), r)),
  login: (identifier: string, password: string) =>
    api<AuthResponse>('/api/auth/login', { body: { identifier, password }, auth: false })
      .then(adoptSession)
      .then((r) => (trackEvent('login', { method: 'password' }), r)),
  register: async (b: { email: string; password: string; username: string; locale: string }) => {
    await freshGuestToken();
    const r = await api<AuthResponse>('/api/auth/register', { body: b, auth: false }).then(adoptSession);
    trackEvent('sign_up', { method: 'email' });
    return r;
  },
  oauth: async (provider: 'google' | 'apple', idToken: string, locale: string) => {
    await freshGuestToken();
    const r = await api<AuthResponse>('/api/auth/oauth', { body: { provider, idToken, locale }, auth: false }).then(adoptSession);
    trackEvent('login', { method: provider });
    return r;
  },
  /** CrazyGames build: sign in with the CrazyGames account (a guest playing now becomes it). */
  crazygames: async (token: string, locale: string) => {
    await freshGuestToken();
    const r = await api<AuthResponse>('/api/auth/crazygames', { body: { token, locale }, auth: false }).then(adoptSession);
    trackEvent('login', { method: 'crazygames' });
    return r;
  },
  async logout() {
    const stored = await tokenStore.get();
    await rawFetch('/api/auth/logout', { method: 'POST', headers: baseHeaders(), body: JSON.stringify(stored ? { refreshToken: stored } : {}) }).catch(() => {});
    accessToken = null;
    accessExpiresAt = 0;
    hint.set(false);
    await tokenStore.clear();
  },
};
